import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogClose, MatDialogRef } from '@angular/material/dialog';
import { MatFormField } from '@angular/material/form-field';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatOption, MatSelect } from '@angular/material/select';
import { TranslatePipe } from '@ngx-translate/core';
import { NotificationService } from '@rms/core/notification';
import { CloseButtonComponent, CONFIRM_RESULT } from '@rms/sdk/dialog';
import { AppError } from '@rms/sdk/errors';
import { ToastComponent } from '@rms/ui/toast';
import { BehaviorSubject, filter, finalize, map, Observable, of, startWith } from 'rxjs';

import { PARTNER_USER_ROLE, PARTNER_USER_ROLE_LABELS, PartnerRole, PartnerUser } from '../../models';
import { PartnerService } from '../../services';
import { PartnerUserTarget } from '../partner-user-dialog.model';

@Component({
  selector: 'rms-partner-user-role',
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    MatDialogClose,
    MatFormField,
    MatButton,
    MatProgressSpinner,
    MatSelect,
    MatOption,
    TranslatePipe,
    CloseButtonComponent,
    ToastComponent,
  ],
  templateUrl: './partner-user-role.component.html',
})
export class PartnerUserRoleComponent implements OnInit {
  protected readonly data = inject<PartnerUserTarget>(MAT_DIALOG_DATA);
  protected readonly roleLabels = PARTNER_USER_ROLE_LABELS;
  protected readonly roles$ = new BehaviorSubject<PartnerRole[]>([]);
  protected readonly loading$ = new BehaviorSubject<boolean>(false);
  protected readonly error$ = new BehaviorSubject<string | null>(null);
  protected readonly role = new FormControl<string | null>(null, Validators.required);
  protected readonly form = new FormGroup({ role: this.role });
  protected readonly changed$: Observable<boolean> = this.role.valueChanges.pipe(
    startWith(null),
    map(() => this.role.value !== this.currentRoleId)
  );

  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogRef = inject<MatDialogRef<PartnerUserRoleComponent, PartnerUser>>(MatDialogRef);
  private readonly partnerService = inject(PartnerService);
  private readonly notificationService = inject(NotificationService);
  private currentRoleId: string | null = null;

  public ngOnInit(): void {
    this.partnerService
      .getPartnerRoles(this.data.scope)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (roles) => {
          this.currentRoleId = roles.find(({ name }) => name === this.data.user.role)?.id ?? null;
          this.roles$.next(roles);
          this.role.setValue(this.currentRoleId);
        },
        error: (error: AppError) => this.error$.next(error.message),
      });
  }

  protected submit(): void {
    const roleId = this.role.value;

    if (!roleId || roleId === this.currentRoleId) {
      return;
    }

    this.confirmDemotion(roleId)
      .pipe(filter(Boolean), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.save(roleId));
  }

  private confirmDemotion(roleId: string): Observable<boolean> {
    const { user } = this.data;
    const demoted =
      user.role === PARTNER_USER_ROLE.ADMIN &&
      this.roles$.value.find(({ id }) => id === roleId)?.name !== PARTNER_USER_ROLE.ADMIN;

    if (!demoted) {
      return of(true);
    }

    return this.notificationService
      .confirm({
        title: { text: 'partners.users.role.demote.title', payload: { name: user.full_name } },
        message: { text: 'partners.users.role.demote.message' },
      })
      .pipe(map((result) => result === CONFIRM_RESULT.OK));
  }

  private save(roleId: string): void {
    const { scope, user } = this.data;
    this.error$.next(null);
    this.loading$.next(true);

    this.partnerService
      .updatePartnerUserRole(scope, user.id, roleId)
      .pipe(
        finalize(() => this.loading$.next(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (updated) => {
          this.notificationService.notify('partners.users.role.success', 'common.ok');
          this.dialogRef.close(updated);
        },
        error: (error: AppError) => this.error$.next(error.message),
      });
  }
}
